// @ts-nocheck
// #region ==================== IMPORTS ====================
import React, { useState, useEffect } from 'react';
import { Delete, Edit, FileUpload } from '@mui/icons-material';
import {
  Button,
  IconButton,
  Stack,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
  Paper,
  Input,
} from '@mui/material';
import { DataGrid, GridRenderCellParams } from '@mui/x-data-grid';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import AtomAutocomplete from '@/components/atoms/Autocomplete';
import AtomButton from '@/components/atoms/Button';
import AtomLineChart from '@/components/atoms/LineChart';
import AtomSelect from '@/components/atoms/Select';
import AtomSlider from '@/components/atoms/Slider';
import AtomTabs from '@/components/atoms/Tabs';
import AtomTextField from '@/components/atoms/TextField';
import { DataTable } from '@/components/organisms/DataMUITable';
import OrgDialog from '@/components/organisms/Dialog';
import RichTextEditor from '@/components/organisms/RichTextEditor';
import { useSafeEffect } from '@/hooks/useSafeEffect';
import actionStyles from '@/pages/Company/Actions/action.module.css';
import { AppDispatch } from '@/store';
import { selectUser } from '@/store/auth/selectors';
import { briefings } from '@/store/briefings/thunks';
import { companies } from '@/store/companies/thunks';
// import { differentiators } from '@/store/differentiators';
import { contact } from '@/store/contact/thunks';
import {
  selectFinancialsData,
  selectFinancialsLoading,
  selectFinancialsError,
} from '@/store/financials/selectors';
import { financials } from '@/store/financials/thunks';
import {
  getStudyId,
  getAllStudiesCompany,
  getStudyName,
  getYearSelected,
} from '@/store/studies/selectors';
import {
  selectCurrentCompany,
  selectCompanyVersions,
  selectCompanyHistory,
  selectCompanyContacts,
  selectStudyYearCompaniesLoading,
} from '@/store/study-year-companies/selectors';
import { studyYearCompanies } from '@/store/study-year-companies/thunks';
// import { survey } from '@/store/survey/thunks';
import { getAllUser, user } from '@/store/user';
import { StudyCompanyHistory } from '@/types/study-companies';
import SegmentsDialogContent from './SegmentsDialogContent';
// #endregion

const ContactDataDefault = {
  email: '',
  first_name: '',
  last_name: '',
  title: '',
  department: '',
  region: 'NA',
  user_id: '',
};

interface CompanyDialogContentProps {
  company: any;
  action: string;
  onClose: () => void;
}

interface ChartDataPoint {
  x: any;
  y: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function transformData(input: StudyCompanyHistory[], key: string): ChartDataPoint[] {
  const sorted = [...input].sort((a, b) => a.display_year - b.display_year);
  return sorted.map(item => ({
    x: item.display_year,
    y: item[key],
  }));
}

function transformToChartData(data: any[]): ChartDataPoint[] {
  // move this to a utility file if used elsewhere
  const quarterToMonth: Record<string, number> = {
    1: 0, // January
    2: 3, // April
    3: 6, // July
    4: 9, // October
  };

  if (!data || data.length === 0) {
    return [];
  }

  return data
    .map(point => {
      // Create a Date object for the start of the quarter
      const date = new Date(point.year, quarterToMonth[point.quarter], 1);

      return {
        x: date,
        y: point.revenue,
      };
    })
    .sort((a, b) => a.x.getTime() - b.x.getTime()); // Sort by date
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`scrollable-auto-tabpanel-${index}`}
      aria-labelledby={`scrollable-auto-tab-${index}`}
      {...other}
    >
      {value === index && <div>{children}</div>}
    </div>
  );
}

export const CompanyDialogContent: React.FC<CompanyDialogContentProps> = ({
  company,
  action,
  onClose,
}) => {
  // #region ==================== Dispatch ====================
  const dispatch = useDispatch<AppDispatch>();
  // #endregion

  // #region ===================== States ====================
  const [differentiatorData, setDifferentiatorData] = useState({
    categorySelection: '',
    editorContent: null,
    title: '',
  });
  const [briefingState, setBriefingState] = useState({
    startTime: null, // This will be a dayjs object
    endTime: null, // This will be a dayjs object
    subject: '',
    description: '',
    userAttendees: [] as PersonSchema[],
    contactAttendees: [] as PersonSchema[],
    notes: '',
    hasTeamsEvent: false,
  });

  const [editingContact, setEditingContact] = useState(null);
  const [editContactOpen, setEditContactOpen] = useState(false);

  const [industryWeight, setIndustryWeight] = useState(0.25);
  const [regionWeight, setRegionWeight] = useState(0.25);
  const [financialsWeight, setFinancialsWeight] = useState(0.25);
  const [totalWeight, setTotalWeight] = useState(0.25);

  const [contactOpen, setContactOpen] = useState(false);
  const [contactData, setContactData] = useState<ContactAdd>(ContactDataDefault);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [contactDeleteId, setContactDeleteId] = useState('');
  const [selectedContacts, setSelectedContacts] = useState(null);

  const [editTab, setEditTab] = useState(1);
  const [revenueValue, setRevenueValue] = useState(0);
  // const [growthRate, setGrowthRate] = useState(0);
  const [isFromSupplier, setIsFromSupplier] = useState(false);

  const [noteContent, setNoteContent] = useState('');
  const [companyProfile, setCompanyProfile] = useState('');

  const [unitsSold, setUnitsSold] = useState(0);
  const [sellingPrice, setSellingPrice] = useState(0);

  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // #endregion

  // #region ==================== Selectors ==================
  const useStudyId = useSelector(getStudyId);
  const useStudyName = useSelector(getStudyName);
  const useSelectedYear = useSelector(getYearSelected);
  const useAllStudiesCompany = useSelector(getAllStudiesCompany);
  const useContacts = useSelector(selectCompanyContacts);
  const useContactsLoading = useSelector(selectStudyYearCompaniesLoading);
  const useAllUser = useSelector(getAllUser);
  const useSelectUser = useSelector(selectUser);
  const useFinancialsData = useSelector(selectFinancialsData);
  const useFinancialsLoading = useSelector(selectFinancialsLoading);
  const useFinancialsError = useSelector(selectFinancialsError);
  const useStudyYearCompanyProfile = useSelector(selectCurrentCompany);
  const useStudyYearCompanyHistory = useSelector(selectCompanyHistory);
  const useStudyYearCompanyVersions = useSelector(selectCompanyVersions);
  // #endregion

  // #region ==================== Variables ==================
  const contactColumns = [
    { field: 'first_name', headerName: 'First Name', flex: 1 },
    { field: 'last_name', headerName: 'Last Name', flex: 1 },
    { field: 'email', headerName: 'Email', flex: 2 },
    {
      field: 'edit',
      type: 'actions',
      width: 80,
      renderCell: (params: GridRenderCellParams) => (
        <IconButton
          onClick={() => {
            setEditingContact(params.row);
            setContactData(params.row);
            setEditContactOpen(true);
          }}
        >
          <Edit />
        </IconButton>
      ),
    },
    {
      field: 'delete',
      type: 'actions',
      width: 80,
      renderCell: (params: GridRenderCellParams) => (
        <IconButton
          onClick={() => {
            handleDeleteOpen(params.row.id);
          }}
        >
          <Delete />
        </IconButton>
      ),
    },
  ];

  const versionsColumns = [
    {
      id: 'id',
      label: 'Version ID',
      minWidth: 200,
      sortable: true,
    },
    {
      id: 'obj_id',
      label: 'Object ID',
      minWidth: 200,
      sortable: true,
    },
    {
      id: 'time_created',
      label: 'Created Date',
      minWidth: 180,
      sortable: true,
      format: value => {
        const date = new Date(value);
        return (
          <div>
            <div>{date.toLocaleDateString('en-GB')}</div>
            <div style={{ fontSize: '0.8em', color: '#666' }}>
              {date.toLocaleTimeString('en-GB')}
            </div>
          </div>
        );
      },
    },
    {
      id: 'data',
      label: 'Data Preview',
      minWidth: 250,
      format: value => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'monospace',
            backgroundColor: '#f5f5f5',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '0.75rem',
          }}
        >
          {JSON.stringify(value).length > 50
            ? JSON.stringify(value).substring(0, 50) + '...'
            : JSON.stringify(value)}
        </Typography>
      ),
    },
  ];
  // #endregion

  const renderVersionDetail = row => (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Version Details
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="subtitle2" color="text.secondary">
            Version ID
          </Typography>
          <Typography variant="body2">{row.id}</Typography>
        </Box>
        <Box>
          <Typography variant="subtitle2" color="text.secondary">
            Object ID
          </Typography>
          <Typography variant="body2">{row.obj_id}</Typography>
        </Box>
      </Box>

      <Typography variant="subtitle2" gutterBottom>
        Full Data:
      </Typography>
      <Paper sx={{ p: 2, bgcolor: '#f8f9fa', maxHeight: 400, overflow: 'auto' }}>
        <pre
          style={{
            margin: 0,
            fontSize: '12px',
            lineHeight: '1.4',
            whiteSpace: 'pre-wrap',
          }}
        >
          {JSON.stringify(row.data, null, 2)}
        </pre>
      </Paper>
    </Box>
  );

  const handleCloseDialog = () => {
    onClose();
  };

  const handleCloseContact = () => {
    setContactOpen(false);
    setEditContactOpen(false);
    setEditingContact(null);
    setContactData(ContactDataDefault);
  };

  const handleDeleteOpen = id => {
    setContactDeleteId(id);
    setDeleteOpen(true);
  };

  const handleCloseDelete = () => {
    setDeleteOpen(false);
  };

  const handleEditTabChange = (event: React.SyntheticEvent, newValue: any) => {
    console.log('Tab change received:', newValue);

    let tabIndex;
    if (typeof newValue === 'object' && newValue.label) {
      // Map the tab object to its index
      const tabLabels = [
        'Revenue',
        'Completed Model Settings',
        'Units and Selling Price',
        'Profile and Notes',
      ];
      tabIndex = tabLabels.findIndex(label => label === newValue.label) + 1; // +1 for 1-based indexing
    } else {
      tabIndex = newValue;
    }

    console.log('Setting editTab to:', tabIndex);
    setEditTab(tabIndex);
  };

  const handleRevenueChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value } = e.target;
    setRevenueValue(value === '' ? 0 : value);
  };

  // const handleGrowthRateChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
  //   const { name, value } = e.target;
  //   const revenue = company.row.py_revenue * (1 + value / 100);

  //   setRevenueValue(revenue.toFixed(2));
  //   setGrowthRate(value);
  // };

  //------------------ EDIT COMPANY ------------------

  const handleSubmit = async () => {
    const data = {
      user_id: useSelectUser.id,
      id: company.row.id,
      study_year_company_id: company.row.study_year_company_id,
    };
    switch (editTab) {
      case 1:
        data.revenue = parseFloat(revenueValue);
        // data.growth_rate = growthRate;
        data.supplier_status = isFromSupplier ? 'confirmed' : 'modelled';
        break;
      case 2:
        console.log('Weight values:', {
          regionWeight,
          industryWeight,
          financialsWeight,
          totalWeight,
        }); // Debug

        data.model_weights = {
          region: regionWeight,
          industry: industryWeight,
          financials: financialsWeight,
          total_market: totalWeight,
        };
        break;
      case 3:
        data.units_sold = unitsSold;
        data.selling_price = sellingPrice;
        break;
      case 4:
        data.note = noteContent;
        data.profile = companyProfile;
        break;
    }
    try {
      await dispatch(
        studyYearCompanies.updateStudyYearCompany({
          studyYearCompanyId: company.row.study_year_company_id,
          ...data,
        })
      );
    } catch (error) {
      console.error('Error updating company:', error);
    }
  };

  // -------------------- Differentiator Action Section --------------------
  const handleDifferentiatorInputChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | { target: { name: string; value: string } }
  ) => {
    const { name, value } = e.target;
    // For AtomSelect
    if (name === 'simple-select') {
      setDifferentiatorData(prev => ({
        ...prev,
        categorySelection: value,
      }));
    } else if (name === 'title') {
      setDifferentiatorData(prev => ({
        ...prev,
        title: value,
      }));
    }
  };

  const handleEditorChange = data => {
    setDifferentiatorData(prev => ({
      ...prev,
      editorContent: data,
    }));
  };

  // const submitDifferentiator = async () => {
  //   try {
  //     // const editorText =
  //     //   differentiatorData.editorContent?.blocks
  //     //     ?.map(block => block.data.text)
  //     //     .filter(Boolean)
  //     //     .join('\n') || '';

  //     await dispatch(
  //       differentiators.createDifferentiator({
  //         company_id: company.row.id,
  //         study_id: useStudyId,
  //         text: differentiatorData.editorContent.blocks,
  //         category_id: differentiatorData.categorySelection,
  //         title: differentiatorData.title,
  //       })
  //     );

  //     clearDifferentiatorForm();
  //   } catch (error) {
  //     console.error('Error submitting differentiator:', error);
  //   }
  // };

  const clearDifferentiatorForm = () => {
    setDifferentiatorData({
      categorySelection: '',
      editorContent: null,
      title: '',
    });
    handleCloseDialog();
  };

  // #region =================== Briefing Action ==================
  const handleBriefingStartTime = (date: any) => {
    setBriefingState(prev => ({
      ...prev,
      startTime: date,
    }));
  };

  const handleBriefingEndTime = (date: any) => {
    setBriefingState(prev => ({
      ...prev,
      endTime: date,
    }));
  };

  const handleBriefingSubject = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setBriefingState(prev => ({
      ...prev,
      subject: e.target.value,
    }));
  };

  const handleNotesChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setBriefingState(prev => ({
      ...prev,
      notes: e.target.value,
    }));
  };

  const handleDescriptionChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setBriefingState(prev => ({
      ...prev,
      description: e.target.value,
    }));
  };

  const handleUserAttendeesChange = (_, newValue) => {
    setBriefingState(prev => ({
      ...prev,
      userAttendees: newValue, // Store the full user objects
    }));
  };

  const handleContactAttendeesChange = (_, newValue) => {
    setBriefingState(prev => ({
      ...prev,
      contactAttendees: newValue, // Store the full contact objects
    }));
  };

  const submitBriefingData = async () => {
    try {
      // Validate required fields
      if (!briefingState.startTime || !briefingState.endTime) {
        console.error('Start time and end time are required');
        return;
      }

      // Prepare user attendees in the correct format
      const userAttendees = briefingState.userAttendees.map(user => ({
        id: user.id,
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
      }));

      // Prepare contact attendees in the correct format
      const contactAttendees = briefingState.contactAttendees.map(contact => ({
        id: contact.id,
        first_name: contact.first_name,
        last_name: contact.last_name,
        email: contact.email,
      }));

      const briefingPayload = {
        user_id: useSelectUser.id,
        owner_id: useSelectUser.id,
        start_time: briefingState.startTime.toISOString(), // Convert dayjs to ISO string
        end_time: briefingState.endTime.toISOString(), // Convert dayjs to ISO string
        subject: briefingState.subject,
        description: briefingState.description,
        company_id: company.row.company_id || company.row.id,
        user_attendees: userAttendees,
        contact_attendees: contactAttendees,
        notes: briefingState.notes,
        study_id: useStudyId,
        study_year_id: useSelectedYear.id, // Make sure you have this from your selectors
        has_teams_event: briefingState.hasTeamsEvent,
      };

      await dispatch(briefings.createBriefing(briefingPayload));

      resetBriefingForm();
    } catch (error) {
      console.error('Error scheduling briefing:', error);
    }
  };

  const resetBriefingForm = () => {
    setBriefingState({
      startTime: null,
      endTime: null,
      subject: '',
      description: '',
      userAttendees: [],
      contactAttendees: [],
      notes: '',
      hasTeamsEvent: false,
    });
    handleCloseDialog();
  };

  // -------------------- Contact Action Section --------------------

  const handleContactChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const newData = { ...contactData, [name]: value };
    setContactData(newData);
  };

  const handleAddContact = async () => {
    try {
      await dispatch(
        contact.createContact({
          studyName: useStudyName, // Use the actual study name from your state
          year: useSelectedYear, // Use the actual year from your state
          companyName: company.row.company_name, // Use the company name from the row
          user_id: useSelectUser.id,
          first_name: contactData.first_name,
          last_name: contactData.last_name,
          email: contactData.email,
          company_id: company.row.id,
          company_name: company.row.company_name,
          title: contactData.title,
        })
      );

      // Refresh the contacts list after adding
      await dispatch(
        contact.getContacts({
          company_name__eq: company.row.company_name,
        })
      );

      setContactOpen(false);
      setContactData(ContactDataDefault); // Reset form
    } catch (error) {
      console.error('Error adding contact:', error);
    }
  };

  const handleDeleteContact = async (contactId: string) => {
    try {
      await dispatch(
        contact.deleteContact({
          studyName: useStudyName,
          year: useSelectedYear,
          companyName: company.row.company_name,
          contactId: contactId,
        })
      );

      // Refresh the contacts list after deletion
      await dispatch(
        contact.getContacts({
          company_name__eq: company.row.company_name,
        })
      );

      setDeleteOpen(false);
    } catch (error) {
      console.error('Error deleting contact:', error);
    }
  };

  const handleUpdateContact = async () => {
    try {
      await dispatch(
        contact.updateContact({
          studyName: useStudyName,
          year: useSelectedYear,
          companyName: company.row.company_name,
          user_id: useSelectUser.id,
          first_name: contactData.first_name,
          last_name: contactData.last_name,
          email: contactData.email,
          company_id: company.row.id,
          company_name: company.row.company_name,
          title: contactData.title,
        })
      );

      // Refresh the contacts list after updating
      await dispatch(
        contact.getContacts({
          company_name__eq: company.row.company_name,
        })
      );

      setEditContactOpen(false);
      setContactData(ContactDataDefault); // Reset form
      setEditingContact(null);
    } catch (error) {
      console.error('Error updating contact:', error);
    }
  };
  // #endregion

  const contactOptions = useContacts.map(contact => ({
    label: `${contact.first_name} ${contact.last_name} (${contact.email})`,
    value: contact.id,
    contact: contact,
  }));

  // #region =================== Survey Action ==================

  const handleSurveyContactChange = (event, newValue) => {
    setSelectedContacts(newValue);
  };

  const sendSurveyForm = async () => {
    try {
      await dispatch(
        survey.sendSurvey({
          study_id: useStudyId,
          company_id: company.row.id,
          contact_id: selectedContacts.contact.id,
        })
      );
      setContactOpen(false);
    } catch (error) {
      console.error('Error adding contact:', error);
    }
  };

  // #endregion

  // #region ================== FUNCTIONS ================

  // -------------------- Upload Survey Handlers --------------------
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type (Excel files)
      const allowedTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
        'application/vnd.ms-excel', // .xls
      ];

      if (!allowedTypes.includes(file.type)) {
        setUploadError('Please select a valid Excel file (.xlsx or .xls)');
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        // 10MB limit
        setUploadError('File size must be less than 10MB');
        return;
      }

      setUploadFile(file);
      setUploadError(null);
    }
  };

  const handleUploadSubmit = async () => {
    if (!uploadFile) {
      setUploadError('Please select a file to upload');
      return;
    }

    setUploadLoading(true);
    setUploadError(null);

    try {
      const uploadRequest: UploadSurveyRequest = {
        studyYearCompanyId: company.row.study_year_company_id,
        file: uploadFile,
      };

      await dispatch(studyYearCompanies.uploadSurvey(uploadRequest));

      // Reset form and close dialog
      setUploadFile(null);
      setUploadError(null);
      handleCloseDialog();
    } catch (error) {
      console.error('Error uploading survey:', error);
      setUploadError('Failed to upload survey');
    } finally {
      setUploadLoading(false);
    }
  };

  const handleUploadCancel = () => {
    setUploadFile(null);
    setUploadError(null);
    handleCloseDialog();
  };

  //#endregion

  // #region =================== Fetch Data ==================
  useSafeEffect(async () => {
    const initAction = async () => {
      console.log(action);
      try {
        switch (action) {
          case 'edit':
            await dispatch(
              studyYearCompanies.getStudyYearCompany({
                studyYearCompanyId: company.row.study_year_company_id,
              })
            );

            // REMOVE all the setState calls from here - they'll go in a separate useEffect
            setRevenueValue(company.row.revenue?.toFixed(2));
            // setGrowthRate((company.row.yoy_revenue_percentage * 100).toFixed(2));
            break;

          case 'financials':
            await dispatch(
              financials.getFinancials({
                company_id__eq: company.row.company_id || company.row.id,
                year__eq: useSelectedYear,
              })
            );
            break;

          case 'seehistory':
            await dispatch(
              studyYearCompanies.getStudyYearCompanyHistory({
                studyYearCompanyId: company.row.study_year_company_id || company.row.id,
              })
            );
            break;

          case 'seeversions':
            await dispatch(
              studyYearCompanies.getStudyYearCompanyVersions({
                studyYearCompanyId: company.row.study_year_company_id || company.row.id,
              })
            );
            break;

          case 'schedulebriefing':
            await dispatch(user.getUsers());
            await dispatch(
              studyYearCompanies.getStudyYearCompanyContacts({
                studyYearCompanyId: company.row.study_year_company_id || company.row.id,
              })
            );
            break;

          case 'contacts':
            await dispatch(
              studyYearCompanies.getStudyYearCompanyContacts({
                studyYearCompanyId: company.row.study_year_company_id || company.row.id,
              })
            );
            break;

          case 'sendsurvey':
            await dispatch(
              studyYearCompanies.getStudyYearCompany({
                studyYearCompanyId: company.row.study_year_company_id, // or company.row.id
              })
            );
            break;
        }
      } catch (error) {
        console.error('Error:', error);
      }
    };

    await initAction();
  }, [action, dispatch]);

  // ADD this new useEffect to watch for profile data changes
  useEffect(() => {
    if (action === 'edit' && useStudyYearCompanyProfile) {
      // Set values from the API response
      setUnitsSold(useStudyYearCompanyProfile.units_sold || 0);
      setSellingPrice(useStudyYearCompanyProfile.selling_price || 0);
      setNoteContent(useStudyYearCompanyProfile.note || '');
      setCompanyProfile(useStudyYearCompanyProfile.profile || '');
      setRegionWeight(useStudyYearCompanyProfile.model_weights?.region || 0.25);
      setIndustryWeight(useStudyYearCompanyProfile.model_weights?.industry || 0.25);
      setFinancialsWeight(useStudyYearCompanyProfile.model_weights?.financials || 0.25);
      setTotalWeight(useStudyYearCompanyProfile.model_weights?.total_market || 0.25);
    }
  }, [useStudyYearCompanyProfile, action]); // Watch for changes to the profile data

  // #endregion

  // #region =================== Render ==================
  switch (action) {
    case 'financials':
      const financialsChartData = transformToChartData(useFinancialsData);

      const financialsSeries = {
        id: 'financials',
        label: 'Revenues',
        color: '#2196f3',
        data: financialsChartData,
      };

      if (useFinancialsLoading) {
        return (
          <div>
            <h3>Financials</h3>
            <div style={{ padding: '20px', textAlign: 'center' }}>Loading financials data...</div>
          </div>
        );
      }

      if (useFinancialsError) {
        return (
          <div>
            <h3>Financials</h3>
            <div style={{ padding: '20px', color: 'red' }}>
              Error loading financials: {useFinancialsError}
            </div>
          </div>
        );
      }

      return (
        <div>
          <h3>Financials</h3>
          <div style={{ width: '100%', height: '60vh', marginTop: '10px' }}>
            <AtomLineChart
              series={[financialsSeries]}
              showHorizontalGrid={true}
              xLabel="Quarter"
              showGrid={true}
              showTooltip={true}
              showLegend={true}
              formatAsQuarters={true}
            />
          </div>
          {useFinancialsData.length === 0 && (
            <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
              No financial data available for this company.
            </div>
          )}
        </div>
      );

    case 'seehistory':
      const cData = [
        {
          id: 'revenue',
          label: 'Company Revenue',
          color: '#2196f3',
          data: transformData(useStudyYearCompanyHistory, 'cumulative_change_company'),
        },
        {
          id: 'market_total',
          label: 'Market Total',
          color: '#4caf50',
          data: transformData(useStudyYearCompanyHistory, 'cumulative_change_study'),
        },
      ];

      return (
        <div>
          <h3>Historical Revenues</h3>
          <div style={{ width: '100%', height: '60vh' }}>
            <AtomLineChart series={cData} showHorizontalGrid />
          </div>
        </div>
      );

    case 'adddifferentiator':
      return (
        <div>
          <h2 className={actionStyles.header}> Select Acquisition Target for ABB </h2>
          <AtomTextField
            name="title"
            label="Title"
            className={actionStyles.Field}
            value={differentiatorData.title}
            onChange={handleDifferentiatorInputChange}
            placeholder="Enter differentiator title"
          />

          <AtomSelect
            name="simple-select"
            label="Category"
            className={actionStyles.Field}
            value={differentiatorData.categorySelection}
            onChange={handleDifferentiatorInputChange}
            options={useSegments.map(res => ({
              label: res.name,
              options: res.categories.map(category => ({
                label: category.name,
                value: category.id,
              })),
            }))}
          />

          <RichTextEditor onChange={handleEditorChange} data={differentiatorData.editorContent} />

          <AtomButton
            variant="contained"
            color="primary"
            size="large"
            onClick={submitDifferentiator}
            className={actionStyles.DialogAction}
          >
            Save
          </AtomButton>
          <AtomButton
            variant="outlined"
            color="primary"
            size="large"
            onClick={clearDifferentiatorForm}
            className={actionStyles.DialogAction}
          >
            Cancel
          </AtomButton>
        </div>
      );

    case 'seeversions':
      return (
        <div>
          <h3>Company Versions History</h3>
          {/* <DataGrid
            rows={useStudyYearCompanyVersions}
            columns={versionsColumns}
            pageSize={10}
            autoHeight
            disableSelectionOnClick
          /> */}

          <DataTable
            title="Version"
            columns={versionsColumns}
            rows={useStudyYearCompanyVersions}
            loading={false} // You can connect this to your loading state
            collapsible={true}
            pagination={true}
            singleExpand={true} // Only one row expanded at a time
            renderDetail={renderVersionDetail}
            getRowId={row => row.id}
            onExpandChange={(rowId, isExpanded, row) => {
              // console.log('Row expanded:', { rowId, isExpanded, row });
            }}
          />
        </div>
      );

    case 'schedulebriefing':
      return (
        <div>
          <h3>Schedule a briefing</h3>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DemoContainer components={['DateTimePicker', 'DateTimePicker']}>
              <DateTimePicker
                label="Start Date & Time"
                value={briefingState.startTime}
                onChange={handleBriefingStartTime}
                className={actionStyles.DatePickerRight}
                format="DD/MM/YYYY HH:mm"
                disablePast={true} // Prevent selecting past dates
              />
              <DateTimePicker
                label="End Date & Time"
                value={briefingState.endTime}
                onChange={handleBriefingEndTime}
                className={actionStyles.DatePickerLeft}
                format="DD/MM/YYYY HH:mm"
                minDateTime={briefingState.startTime} // End time must be after start time
                disablePast={true}
              />
            </DemoContainer>
          </LocalizationProvider>

          <AtomTextField
            value={briefingState.subject}
            onChange={handleBriefingSubject}
            className={actionStyles.Field}
            label="Subject"
            placeholder="Enter briefing subject"
            required
          />

          <AtomTextField
            value={briefingState.description}
            onChange={handleDescriptionChange}
            className={actionStyles.Field}
            label="Description"
            placeholder="Enter briefing description"
            multiline
            rows={3}
            required
          />

          <AtomTextField
            value={briefingState.notes}
            onChange={handleNotesChange}
            className={actionStyles.Field}
            label="Notes"
            placeholder="Enter additional notes (optional)"
            multiline
            rows={2}
          />

          <AtomAutocomplete
            label="User Attendees"
            placeholderText="Select users"
            multiple
            value={briefingState.userAttendees}
            options={useAllUser.map(user => ({
              id: user.id,
              first_name: user.first_name,
              last_name: user.last_name,
              email: user.email,
              label: `${user.first_name} ${user.last_name} (${user.email})`,
            }))}
            onChange={handleUserAttendeesChange}
            className={actionStyles.Field}
            getOptionLabel={option =>
              option.label || `${option.first_name} ${option.last_name} (${option.email})`
            }
          />

          <AtomAutocomplete
            label="Contact Attendees"
            placeholderText="Select contacts"
            multiple
            value={briefingState.contactAttendees}
            options={useContacts.map(contact => ({
              id: contact.id,
              first_name: contact.first_name,
              last_name: contact.last_name,
              email: contact.email,
              label: `${contact.first_name} ${contact.last_name} (${contact.email})`,
            }))}
            onChange={handleContactAttendeesChange}
            className={actionStyles.Field}
            getOptionLabel={option =>
              option.label || `${option.first_name} ${option.last_name} (${option.email})`
            }
          />

          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={briefingState.hasTeamsEvent}
                  onChange={e =>
                    setBriefingState(prev => ({
                      ...prev,
                      hasTeamsEvent: e.target.checked,
                    }))
                  }
                />
              }
              label="Create Teams Event"
            />
          </FormGroup>

          <AtomButton
            variant="contained"
            color="primary"
            size="large"
            onClick={submitBriefingData}
            className={actionStyles.DialogAction}
            disabled={!briefingState.startTime || !briefingState.endTime || !briefingState.subject}
          >
            Schedule Briefing
          </AtomButton>
          <AtomButton
            variant="outlined"
            color="primary"
            size="large"
            onClick={resetBriefingForm}
            className={actionStyles.DialogAction}
          >
            Cancel
          </AtomButton>
        </div>
      );

    case 'contacts':
      return (
        <div>
          <h3> Contacts </h3>
          <DataGrid rows={useContacts} columns={contactColumns} pageSize={5} />
          <Stack direction="row" spacing={2} marginTop={2}>
            <Button variant="contained" color="primary" onClick={() => setContactOpen(true)}>
              Add Contact
            </Button>
          </Stack>
          <OrgDialog open={contactOpen || editContactOpen} onClose={handleCloseContact}>
            <h2>{editingContact ? 'Edit Contact' : 'Add Contact'}</h2>
            <Stack spacing={2} marginTop={2}>
              <AtomTextField
                label="First Name"
                name="first_name"
                onChange={handleContactChange}
                value={contactData.first_name}
              />
              <AtomTextField
                label="Last Name"
                name="last_name"
                onChange={handleContactChange}
                value={contactData.last_name}
              />
              <AtomTextField
                label="Title"
                name="title"
                onChange={handleContactChange}
                value={contactData.title}
              />
              <AtomTextField
                label="Department"
                name="department"
                onChange={handleContactChange}
                value={contactData.department}
              />
              <AtomTextField
                label="Email"
                name="email"
                onChange={handleContactChange}
                value={contactData.email}
              />
            </Stack>

            <Stack direction="row" spacing={2} marginTop={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={editingContact ? handleUpdateContact : handleAddContact}
              >
                {editingContact ? 'Update' : 'Add'}
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => {
                  setContactOpen(false);
                  setEditContactOpen(false);
                  setEditingContact(null);
                  setContactData(ContactDataDefault);
                }}
              >
                Cancel
              </Button>
            </Stack>
          </OrgDialog>

          <OrgDialog open={deleteOpen} onClose={handleCloseDelete}>
            <h2> Are you sure you want to delete this contact? </h2>
            <Stack direction="row" spacing={2} marginTop={2}>
              <Button
                variant="contained"
                color="error"
                onClick={() => handleDeleteContact(contactDeleteId)}
              >
                Delete
              </Button>
              <Button variant="outlined" color="primary" onClick={handleCloseDelete}>
                Cancel
              </Button>
            </Stack>
          </OrgDialog>
        </div>
      );

    case 'sendsurvey':
      return (
        <div>
          <h3>Send Survey</h3>

          <AtomAutocomplete
            label="Contacts"
            placeholderText="Search contacts"
            value={selectedContacts}
            options={contactOptions}
            className={actionStyles.Field}
            onChange={handleSurveyContactChange}
            loading={useContactsLoading}
            renderOption={(props, option) => <li {...props}>{option.label}</li>}
            getOptionLabel={option => option?.label || ''}
            isOptionEqualToValue={(option, value) => option?.value === value?.value}
          />

          <AtomButton
            variant="contained"
            color="primary"
            size="large"
            onClick={sendSurveyForm}
            className={actionStyles.DialogAction}
            disabled={!selectedContacts || useContactsLoading}
          >
            {useContactsLoading ? 'Loading...' : 'Send Survey'}
          </AtomButton>
          <AtomButton
            variant="outlined"
            color="primary"
            size="large"
            onClick={handleCloseDialog}
            className={actionStyles.DialogAction}
          >
            Cancel
          </AtomButton>
        </div>
      );

    case 'edit':
      if (!useStudyYearCompanyProfile) {
        return (
          <div style={{ padding: '20px', textAlign: 'center' }}>Loading company profile...</div>
        );
      }

      return (
        <div className={actionStyles.contentSpace}>
          <AtomTabs
            value={editTab}
            onChange={handleEditTabChange}
            tabs={[
              { label: 'Revenue' },
              { label: 'Completed Model Settings' },
              { label: 'Units and Selling Price' },
              { label: 'Profile and Notes' },
            ]}
            variant="fullWidth"
          >
            <div>
              <div className={actionStyles.editDialogContainer}>
                <div className={actionStyles.editInputContainer}>
                  <div className={actionStyles.editRevenueSection}>
                    <h3>Input Revenue</h3>
                    <div className={actionStyles.editInputField}>
                      <AtomTextField
                        label="Revenue"
                        name="revenue"
                        onChange={handleRevenueChange}
                        defaultValue={company.row.revenue}
                        value={revenueValue}
                        fullWidth
                      />
                    </div>
                  </div>
                  {/* <div className={actionStyles.editSeparator}>
                    <div></div>
                    <h4>OR</h4>
                    <div></div>
                  </div> */}
                  {/* <div className={actionStyles.editGrowthSection}>
                    <h3>Input Growth</h3>
                    <div className={actionStyles.editSliderContainer}>
                      <AtomSlider
                        value={growthRate}
                        defaultValue={company.row.yoy_revenue_percentage * 100}
                        onChange={handleGrowthRateChange}
                        min={-30}
                        max={30}
                        step={0.5}
                        valueLabelDisplay="auto"
                        valueLabelFormat={value => `${value}%`}
                        name="growthRate"
                        label="Growth Rate"
                        className={actionStyles.Slider}
                        style={{
                          width: '100%',
                        }}
                      />
                      <Input
                        value={growthRate}
                        onChange={handleGrowthRateChange}
                        inputProps={{
                          step: 0.01,
                          min: -25,
                          max: 25,
                          type: 'number',
                          'aria-labelledby': 'input-slider',
                        }}
                        endAdornment="%"
                      />
                    </div>
                  </div> */}
                </div>
              </div>

              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isFromSupplier}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setIsFromSupplier(e.target.checked)
                      }
                    />
                  }
                  label="Is this number coming directly from a supplier?"
                />
              </FormGroup>
            </div>

            <div>
              <div className={actionStyles.WeightContent}>
                <h3>Model Weights</h3>
                <AtomSlider
                  value={regionWeight}
                  onChange={(e, newValue) => setRegionWeight(newValue)}
                  min={0}
                  max={1}
                  step={0.01}
                  valueLabelDisplay="auto"
                  valueLabelFormat={value => `${value}%`}
                  name="regionWeight"
                  label="Region Weight"
                  className={actionStyles.weightSlider}
                  sx={{
                    width: '100%',
                  }}
                />
                <AtomSlider
                  value={industryWeight}
                  defaultValue={useStudyYearCompanyProfile?.model_weights?.industry}
                  onChange={(e, newValue) => setIndustryWeight(newValue)}
                  min={0}
                  max={1}
                  step={0.01}
                  valueLabelDisplay="auto"
                  valueLabelFormat={value => `${value}%`}
                  name="industryWeight"
                  label="Industry Weight"
                  className={actionStyles.weightSlider}
                  sx={{
                    width: '100%',
                  }}
                />
                <AtomSlider
                  value={financialsWeight}
                  defaultValue={useStudyYearCompanyProfile?.model_weights?.financials}
                  onChange={(e, newValue) => setFinancialsWeight(newValue)}
                  min={0}
                  max={1}
                  step={0.01}
                  valueLabelDisplay="auto"
                  valueLabelFormat={value => `${value}%`}
                  name="financialsWeight"
                  label="Financials Weight"
                  className={actionStyles.weightSlider}
                  sx={{
                    width: '100%',
                  }}
                />
                <AtomSlider
                  value={totalWeight}
                  defaultValue={useStudyYearCompanyProfile?.model_weights?.total_market}
                  onChange={(e, newValue) => setTotalWeight(newValue)}
                  min={0}
                  max={1}
                  step={0.01}
                  valueLabelDisplay="auto"
                  valueLabelFormat={value => `${value}%`}
                  name="totalWeight"
                  label="Total Weight"
                  className={actionStyles.weightSlider}
                  sx={{
                    width: '100%',
                  }}
                />
              </div>
            </div>

            <div>
              <div className={actionStyles.unitContent}>
                <div className={actionStyles.unitElements}>
                  <h3>Units Sold</h3>
                  <AtomTextField value={unitsSold} onChange={e => setUnitsSold(e.target.value)} />
                </div>

                <div>
                  <h3>Selling Price</h3>
                  <AtomTextField
                    value={sellingPrice}
                    onChange={e => setSellingPrice(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div>
              <div className={actionStyles.notesContent}>
                <div className={actionStyles.notesElements}>
                  <h3>Notes</h3>
                  <AtomTextField
                    className={actionStyles.Field}
                    multiline
                    minRows={3}
                    maxRows={6}
                    placeholder="Enter Note"
                    defaultValue={useStudyYearCompanyProfile?.note}
                    value={noteContent}
                    onChange={e => setNoteContent(e.target.value)}
                  />
                </div>

                <div>
                  <h3>Profile</h3>
                  <AtomTextField
                    className={actionStyles.Field}
                    multiline
                    minRows={3}
                    maxRows={6}
                    placeholder="Enter Profile"
                    defaultValue={useStudyYearCompanyProfile?.profile}
                    value={companyProfile}
                    onChange={e => setCompanyProfile(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </AtomTabs>
          <AtomButton
            variant="contained"
            color="primary"
            size="large"
            onClick={handleSubmit}
            className={actionStyles.DialogAction}
          >
            Save
          </AtomButton>
        </div>
      );

    case 'editsegments':
      return (
        <SegmentsDialogContent
          selectedRow={company.row}
          onClose={onClose} // Use the onClose prop instead of closeCompanyActionDialog
          onSuccess={() => {
            // Optionally refresh data after successful update
            // You can call any refresh function here if needed
            onClose(); // Close the dialog after success
          }}
        />
      );

    case 'uploadsurvey':
      return (
        <div>
          <h3>Upload Survey</h3>
          <div style={{ marginBottom: '20px' }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Upload a completed survey file for {company.row.company_name}. Only Excel files
              (.xlsx, .xls) up to 10MB are supported.
            </Typography>
          </div>

          <Box sx={{ mb: 3 }}>
            <input
              accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
              style={{ display: 'none' }}
              id="survey-file-upload"
              type="file"
              onChange={handleFileUpload}
              disabled={uploadLoading}
            />
            <label htmlFor="survey-file-upload">
              <AtomButton
                variant="outlined"
                component="span"
                startIcon={<FileUpload />}
                disabled={uploadLoading}
                fullWidth
                sx={{ mb: 2, p: 3, borderStyle: 'dashed' }}
              >
                {uploadFile ? uploadFile.name : 'Choose Survey File'}
              </AtomButton>
            </label>

            {uploadFile && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Selected: {uploadFile.name} ({Math.round(uploadFile.size / 1024)} KB)
                </Typography>
              </Box>
            )}

            {uploadError && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                {uploadError}
              </Typography>
            )}
          </Box>

          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <AtomButton variant="outlined" onClick={handleUploadCancel} disabled={uploadLoading}>
              Cancel
            </AtomButton>
            <AtomButton
              variant="contained"
              color="primary"
              onClick={handleUploadSubmit}
              disabled={!uploadFile || uploadLoading}
              loading={uploadLoading}
            >
              {uploadLoading ? 'Uploading...' : 'Upload Survey'}
            </AtomButton>
          </Stack>
        </div>
      );
    default:
      return null;
  }
  // #endregion
};
